/**
 * Habit hooks — TanStack Query for habit grid, toggle, CRUD.
 * Optimistic updates on toggle for instant feedback.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  fetchMonthlyHabits,
  fetchHabitProfileStats,
  upsertHabitLog,
  deleteHabitLog,
  createHabit,
  updateHabit,
  deleteHabit,
  fetchCategories,
  checkHabitLimit,
} from '@turnifly/shared/api'
import type { CreateHabitParams, ProofMedia } from '@turnifly/shared/api'
import { MonthlyHabitsResponseSchema } from '@turnifly/shared/types'
import type { Goal } from '@turnifly/shared/types'
import { triggerHapticNotification } from '@turnifly/shared/utils'
import { updateGoal as updateGoalApi } from '@turnifly/shared/api'
import { goalKeys } from './use-goals'

// ── Query Keys ──────────────────────────────────────────────

export const habitKeys = {
  all: ['habits'] as const,
  monthly: (userId: string, year: number, month: number) =>
    ['habits', 'monthly', userId, year, month] as const,
  profile: (habitId: string) => ['habits', 'profile', habitId] as const,
  categories: ['categories'] as const,
  limit: (userId: string) => ['habits', 'limit', userId] as const,
} as const

// ── Monthly Habits ──────────────────────────────────────────

export function useMonthlyHabits(userId: string | undefined, year: number, month: number) {
  return useQuery({
    queryKey: habitKeys.monthly(userId ?? '', year, month),
    queryFn: () => fetchMonthlyHabits({ userId: userId!, year, month }),
    enabled: !!userId,
    staleTime: 30_000, // 30 seconds
    select: (data) => MonthlyHabitsResponseSchema.parse(data),
  })
}

// ── Toggle Habit (Check/Uncheck) ────────────────────────────

export interface ToggleHabitParams {
  userId: string
  habitId: string
  logDate: string
  completed: boolean
  value?: number | null
  proofUrl?: string | null
  proofType?: string | null
  proofUrls?: ProofMedia[]
  proofText?: string | null
}

export function useToggleHabit(year: number, month: number) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['habit-toggle'],
    mutationFn: async (params: ToggleHabitParams) => {
      if (params.completed) {
        await upsertHabitLog({
          userId: params.userId,
          habitId: params.habitId,
          logDate: params.logDate,
          completed: true,
          value: params.value,
          proofUrl: params.proofUrl,
          proofType: params.proofType,
          proofUrls: params.proofUrls,
          proofText: params.proofText,
        })
      } else {
        await deleteHabitLog(params.userId, params.habitId, params.logDate)
      }
    },
    onMutate: async (params) => {
      const key = habitKeys.monthly(params.userId, year, month)
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData(key)

      queryClient.setQueryData(key, (old: unknown) => {
        if (!old || typeof old !== 'object') return old
        const data = old as { habits?: unknown[] }
        if (!Array.isArray(data.habits)) return old

        return {
          ...data,
          habits: (data.habits as Array<Record<string, unknown>>).map((habit) => {
            if (habit.id !== params.habitId) return habit

            const logs = (habit.logs as Array<Record<string, unknown>>) ?? []
            const newLogs = params.completed
              ? [
                  ...logs.filter((l) => l.log_date !== params.logDate),
                  {
                    log_date: params.logDate,
                    completed: true,
                    value: params.value ?? null,
                    proof_url: params.proofUrl ?? null,
                    proof_urls: params.proofUrls ?? [],
                    proof_text: params.proofText ?? null,
                  },
                ]
              : logs.filter((l) => l.log_date !== params.logDate)

            return { ...habit, logs: newLogs }
          }),
        }
      })

      return { previous, key }
    },
    onError: (_err, _params, context) => {
      if (context?.previous && context?.key) {
        queryClient.setQueryData(context.key, context.previous)
      }
      // Haptic feedback so user knows the toggle failed
      triggerHapticNotification('error')
    },
    onSettled: (_data, _error, params) => {
      // Delayed invalidation — give DB triggers time to update streaks/stats + goal progress
      setTimeout(() => {
        void queryClient.invalidateQueries({
          queryKey: habitKeys.monthly(params.userId, year, month),
        })
        // Also refresh goals so we detect achievement (DB trigger updates goal.current_value)
        void queryClient.invalidateQueries({ queryKey: goalKeys.all })
      }, 500)
    },
  })
}

// ── Create Habit ────────────────────────────────────────────

export function useCreateHabit() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: CreateHabitParams) => createHabit(params),
    onSuccess: async (_data, params) => {
      queryClient.invalidateQueries({ queryKey: habitKeys.all })

      // Auto-transition goal status: planned → in_progress
      if (params.goalId) {
        const goalId = params.goalId
        // Check all goal caches for this goal
        const allGoalCaches = queryClient.getQueriesData<Goal[]>({
          queryKey: goalKeys.all,
        })
        let needsTransition = false
        for (const [, goals] of allGoalCaches) {
          const goal = goals?.find((g) => g.id === goalId)
          if (goal?.status === 'planned') {
            needsTransition = true
            break
          }
        }
        if (needsTransition) {
          await updateGoalApi(goalId, { status: 'in_progress' })
          queryClient.invalidateQueries({ queryKey: goalKeys.all })
        }
      }
    },
  })
}

// ── Update Habit ────────────────────────────────────────────

export function useUpdateHabit() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ habitId, updates }: { habitId: string; updates: Record<string, unknown> }) =>
      updateHabit(habitId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: habitKeys.all })
    },
  })
}

// ── Delete Habit (soft) ─────────────────────────────────────

export function useDeleteHabit() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (habitId: string) => deleteHabit(habitId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: habitKeys.all })
    },
  })
}

// ── Habit Profile Stats ─────────────────────────────────────

export function useHabitProfileStats(habitId: string | null) {
  return useQuery({
    queryKey: habitKeys.profile(habitId ?? ''),
    queryFn: () => fetchHabitProfileStats(habitId!),
    enabled: !!habitId,
    staleTime: 60_000,
  })
}

// ── Habit Limit ────────────────────────────────────────────

export function useHabitLimit(userId: string | undefined) {
  return useQuery({
    queryKey: habitKeys.limit(userId ?? ''),
    queryFn: () => checkHabitLimit(userId!),
    enabled: !!userId,
    staleTime: 30_000,
  })
}

// ── Categories ──────────────────────────────────────────────

export function useCategories() {
  return useQuery({
    queryKey: habitKeys.categories,
    queryFn: fetchCategories,
    staleTime: Infinity, // categories never change at runtime
  })
}
